import AxDateUtils from './date-utils';

function makeUTCDate(...args: number[]): Date {
  return AxDateUtils.makeUTCDate(...args);
}

function convertToUTCDate(inputDate: Date): Date {
  return new Date(
    inputDate.getTime() - (inputDate.getTimezoneOffset() * 60000)
  );
}

function convertFromUTCDate(inputUTCDate: Date): Date {
  if (!inputUTCDate) return inputUTCDate;
  return new Date(
    inputUTCDate.getTime() + inputUTCDate.getTimezoneOffset() * 60000
  );
}

type WhichDate = "from" | "to";
interface DatePickerOptions {
  datePicker: HTMLDivElement;
  fromDate?: Date;
  toDate?: Date;
  financialYears?: {
    from: number;
    to: number;
  };
  quarters?: {
    from: [number | string, number | string];
    to: [number | string, number | string];
  };

  inputFieldsInitialValue?: 'blank' | 'dateformat' | 'datevalue';
}

export default class AxDatepicker {
  initialOptions: Object;

  datePicker: HTMLDivElement;
  fromDate: Date;
  toDate: Date;
  sidebars?: string[] | null = null;

  inputFieldsInitialValue?: 'blank' | 'dateformat' | 'datevalue';

  fromDateInputField: HTMLDivElement;
  toDateInputField: HTMLDivElement;
  yearBar: HTMLDivElement;
  yearDisplayText: HTMLSpanElement;
  monthBar: HTMLDivElement;
  monthDisplayText: HTMLSpanElement;
  expansionSidebar: HTMLDivElement;

  workingDateInputField: HTMLDivElement;
  private workingDateBackingProp: Date = makeUTCDate();
  private get workingDate(): Date {
    return this.workingDateBackingProp;
  }
  private setWorkingDate(value: Date|null|undefined) {
    if (!!value) {
      this.workingDateBackingProp = value;
    }
  }
  whichWorkingDate: WhichDate;
  isDateRange: boolean;
  financialYearSidebar: HTMLDivElement | null = null;
  quartersSidebar: HTMLDivElement | null = null;

  // Sidebar options
  fyStart: number;
  fyEnd: number;
  qStart: [number, number];
  qEnd: [number, number];

  // Status flags
  isDateInputError: boolean;
  isDateRangeError: boolean;
  fromDateIsSelected: boolean;
  toDateIsSelected: boolean;

  eventListeners: { [key: string]: Array<(e: CustomEvent) => void> } = {};

  constructor(options:DatePickerOptions){
    if (options.fromDate) {
      this.fromDate = convertToUTCDate(options.fromDate);
    }
    this.setWorkingDate(this.fromDate);

    this.initialOptions = options;
    this.datePicker = options.datePicker;
    this.whichWorkingDate = "from";
    this.inputFieldsInitialValue = options.inputFieldsInitialValue || "dateformat";
    this.isDateRangeError = false;
    this.isDateInputError = false;
    this.eventListeners["change"] = [];
    this.fromDateIsSelected = false;

    if(options.financialYears){
      if (this.isLegitYear(options.financialYears.from) &&
        this.isLegitYear(options.financialYears.to)){
          this.fyStart = options.financialYears.from;
          this.fyEnd = options.financialYears.to;
      } else {
        this.fyStart = this.fyDefaultStart;
        this.fyEnd = this.fyDefaultEnd;
      }
    } else {
      this.fyStart = this.fyDefaultStart;
      this.fyEnd = this.fyDefaultEnd;
    }

    if(options.quarters){
      if(this.isLegitYear(options.quarters.from[0]) &&
        parseInt(options.quarters.from[1] as string)>=1 &&
        parseInt(options.quarters.from[1] as string)<=4 &&
        this.isLegitYear(options.quarters.to[0])&&
        parseInt(options.quarters.to[1] as string)>=1 &&
        parseInt(options.quarters.to[1] as string)<=4){
          this.qStart = [parseInt(options.quarters.from[0] as string),
            parseInt(options.quarters.from[1] as string)];
          this.qEnd = [parseInt(options.quarters.to[0] as string),
            parseInt(options.quarters.to[1] as string)];
      } else {
        this.qStart = this.quartersDefaultStart as [number, number];
        this.qEnd = this.quartersDefaultEnd as [number, number];
      }
    } else {
      this.qStart = this.quartersDefaultStart as [number, number];
      this.qEnd = this.quartersDefaultEnd as [number, number];
    }

    this.fromDateInputField = this.datePicker.querySelector(".ax-date-input--from-field") as HTMLDivElement;
    this.fromDateInputField.setAttribute("contentEditable", "true");
    this.fromDateInputField.setAttribute("contentEditable", "false");
    this.workingDateInputField = this.fromDateInputField;

    if(this.datePicker.querySelector(".ax-date-picker__year-bar") ){
    this.yearBar = this.datePicker.querySelector(".ax-date-picker__year-bar") as HTMLDivElement;}

    if(this.datePicker.querySelector(".ax-date-picker__month-bar") ){
    this.monthBar = this.datePicker.querySelector(".ax-date-picker__month-bar") as HTMLDivElement;}

    // Date range options
    if(this.datePicker.classList.contains("ax-date-range-picker")){
      this.toDateInputField = this.datePicker.querySelector(".ax-date-input--to-field") as HTMLDivElement;
      this.toDateInputField.setAttribute("contentEditable", "true");
      this.toDateInputField.setAttribute("contentEditable", "false");
      this.isDateRange = true;
      this.toDateIsSelected = false;

      if(options.toDate){
        this.toDate = convertToUTCDate(options.toDate);
      }

      // Sidebars
      if(this.datePicker.querySelector(".ax-date-picker__expansion-panel__sidebar")){
        this.expansionSidebar = this.datePicker.querySelector(".ax-date-picker__expansion-panel__sidebar") as HTMLDivElement;
      }
    } else {
      this.isDateRange = false;
    }
  }

  // Range of years to display in the year-selector dropdown
  readonly yearListStart = 1900;
  readonly yearlistEnd = 2035;
  readonly dateInputFormatString = `
    <span class="ax-date-picker__date-value">dd</span>
    <span class="ax-date-picker__delimiter">/</span>
    <span class="ax-date-picker__date-value">mm</span>
    <span class="ax-date-picker__delimiter">/</span>
    <span class="ax-date-picker__date-value">yyyy</span>`;
  readonly fyDefaultStart = 2010;
  readonly fyDefaultEnd = 2026;
  readonly quartersDefaultStart = [2018, 1]; // [yyyy, quarter_number]
  readonly quartersDefaultEnd = [2025, 3];

  // Getters
  public get axDatePickerFromDate(): Date {
    return convertFromUTCDate(this.fromDate);
  }

  public get axDatePickerToDate(): Date {
    return convertFromUTCDate(this.toDate);
  }

  private toggleDatePickerPanelExpanded() {
    AxDatepicker.toggleDatePickerPanelExpanded(this.datePicker, this.workingDateInputField, [this.fromDateInputField, this.toDateInputField]);
  }

  private setDatePickerPanelExpanded(action: 'open' | 'close') {
    AxDatepicker.setDatePickerPanelExpanded(action, this.datePicker, this.workingDateInputField, [this.fromDateInputField, this.toDateInputField]);
  }

  private static toggleDatePickerPanelExpanded(datePicker: HTMLElement, dateField: HTMLElement, allDateFields: Array<HTMLElement>) {
    const action = datePicker.classList.contains("open") ? "close" : "open";
    AxDatepicker.setDatePickerPanelExpanded(action, datePicker, dateField, allDateFields);
  }

  private static setDatePickerPanelExpanded(action: 'open' | 'close', datePicker: HTMLElement, dateField: HTMLElement, allDateFields: Array<HTMLElement>) {
    if(datePicker.classList.contains("disabled")){return}

    if(action === "open") {
      if (!datePicker.classList.contains("open")) {
        datePicker.classList.add(action);
      }

      allDateFields.map(f => f.setAttribute("contentEditable", "false"));
      dateField.setAttribute("contentEditable", "true");
      setTimeout(() => dateField.focus(), 200);
    } else if(action === "close") {
      dateField.setAttribute("contentEditable", "false");
      if (datePicker.classList.contains("open")) {
        datePicker.classList.remove("open");
      }
    }
  }

  private static selectElementText(editableElement: HTMLElement) {
    const range = document.createRange();
    range.selectNodeContents(editableElement);
    const selection = window.getSelection();
    if (selection) {
      selection?.removeAllRanges();
      selection.addRange(range);
    }
  }

  // ------- Actions -------
  private toggleOpenCloseAction(element:HTMLElement){
    if(element.classList.contains('open')){
      element.classList.remove('open')
    }else{
      element.classList.add('open');
    }
  }

  private dateInputFieldUpdateAction(e: KeyboardEvent){
    let dateInputValue: string = '';

    const getDateVals = (dateInputValue: string) => {
      // used for relative date assignments below. New date will be offset from this date
      const referenceDate = makeUTCDate();

      const regexFwdSlash = /^\d{1,2}\/\d{1,2}\/\d{2}(\d{2})?$/; // Fwd-slash test: "dd/mm/yyyy"
      if (regexFwdSlash.test(dateInputValue)) {
        const targetDate = dateInputValue
          .split('/')
          .map((datePart) => (parseInt(datePart, 10)));
        targetDate[1] = targetDate[1] - 1;
        return targetDate;
      }

      const regexDash = /^\d{1,2}\-\d{1,2}\-\d{2}(\d{2})?$/;  // Dash test: "DD-MM-YYYY"
      if (regexDash.test(dateInputValue)) {
        const targetDate = dateInputValue
          .split('-')
          .map((datePart) => (parseInt(datePart, 10)));
        targetDate[1] = targetDate[1] - 1;
        return targetDate;
      }

      const regexSpace = /^\d{1,2}\s\d{1,2}\s\d{2}(\d{2})?$/; // Space test: "DD MM YYYY"
      if (regexSpace.test(dateInputValue)) {
        const targetDate = dateInputValue
          .split(' ')
          .map((datePart) => parseInt(datePart, 10));
        targetDate[1] = targetDate[1] - 1;
        return targetDate;
      }

      const regexDot = /^\d{1,2}\.\d{1,2}\.\d{2}(\d{2})?$/; // Dot test: "DD.MM.YYYY"
      if (regexDot.test(dateInputValue)) {
        const targetDateTuple = dateInputValue
          .split('.')
          .map((datePart) => parseInt(datePart, 10));
        targetDateTuple[1] = targetDateTuple[1] - 1;
        return targetDateTuple;
      }

      const regexComma = /^\d{1,2}\,\d{1,2}\,\d{2}(\d{2})?$/; // Comma test: "DD,MM,YYYY"
      if (regexComma.test(dateInputValue)) {
        const targetDate = dateInputValue
          .split(',')
          .map((datePart) => parseInt(datePart, 10));
        targetDate[1] = targetDate[1] - 1;
        return targetDate;
      }

      const regexNoDelim = /^\d{8}$/; // No delimiter test: "DDMMYYYY"
      if (regexNoDelim.test(dateInputValue)) {
        const targetDateTuple = [
          parseInt(dateInputValue.slice(0, 2),10),
          parseInt(dateInputValue.slice(2, 4),10),
          parseInt(dateInputValue.slice(4),10)];
        targetDateTuple[1] = targetDateTuple[1] - 1;
        return targetDateTuple;
      }

      const regexTNChar = /^([TtNn])$/i; // "T" or "N" key
      if(regexTNChar.test(dateInputValue)){
        const targetDate = makeUTCDate();
        return [
          targetDate.getUTCDate(),
          targetDate.getUTCMonth(),
          targetDate.getUTCFullYear(),
        ];
      }

      const regex2or3Chars = /^[a-zA-Z]{2,}$/; // Day of the week input (eg: "Mon", "mon", "Monday" or "monday")
      if (
        regex2or3Chars.test(dateInputValue) &&
        AxDateUtils.DAYS_OF_WEEK.some((day) =>
          day.toLowerCase().includes(dateInputValue.toLowerCase())
        )
      ) {
        const targetDayIndex = AxDateUtils.DAYS_OF_WEEK.findIndex((day) =>
          day.toLowerCase().startsWith(dateInputValue.toLowerCase())
        );
        const currentDayIndex = referenceDate.getUTCDay();
        const dateDiff = targetDayIndex - currentDayIndex;
        const targetDate = new Date(referenceDate);
        targetDate.setUTCDate(targetDate.getUTCDate() + dateDiff);
        return [
          targetDate.getUTCDate(),
          targetDate.getUTCMonth(),
          targetDate.getUTCFullYear(),
        ];
      }
      if (
        regex2or3Chars.test(dateInputValue) &&
        AxDateUtils.MONTHS.some((month) =>
          month.toLowerCase().includes(dateInputValue.toLowerCase())
        )
      ) {
        const targetMonthIndex = AxDateUtils.MONTHS.findIndex((month) =>
          month.toLowerCase().startsWith(dateInputValue.toLowerCase())
        );
        const currentMonthIndex = referenceDate.getUTCMonth();
        const monthDiff = targetMonthIndex - currentMonthIndex;
        const targetDate = new Date(referenceDate);
        return [
          targetDate.getUTCDate(),
          targetDate.getUTCMonth() + monthDiff,
          targetDate.getUTCFullYear(),
        ];
      }

      const regexSteps = /^[Tt]?[+-]\d{1,3}?/; // '+' or '-' input, eg: (+2, -3, T-1, T+5)
      if (regexSteps.test(dateInputValue)) {
        let arg = dateInputValue;
        if (arg[0].toLowerCase() === 't') {
          arg = arg.slice(1);
        }
        const targetDate = new Date(referenceDate);
        if (arg[0] === '+') {
          targetDate.setUTCDate(
            targetDate.getUTCDate() + parseInt(arg.slice(1))
          );
        }
        if (arg[0] === '-') {
          targetDate.setUTCDate(
            targetDate.getUTCDate() - parseInt(arg.slice(1))
          );
        }
        return [
          targetDate.getUTCDate(),
          targetDate.getUTCMonth(),
          targetDate.getUTCFullYear(),
        ];
      }

      // Prev & next occurence of date (eg:"last July", "Next Monday")
      const regexNextOccurence = /^(Next|next|Last|last) [a-zA-Z]+$/i;
      if (regexNextOccurence.test(dateInputValue)) {
        const direction = dateInputValue.split(' ')[0].toLowerCase();
        const target = dateInputValue.split(' ')[1].toLowerCase();
        const today = referenceDate;

        // Test for day of week
        if (
          regex2or3Chars.test(target) &&
          AxDateUtils.DAYS_OF_WEEK.some((day) =>
            day.toLowerCase().includes(target.toLowerCase())
          )
        ) {
          const chosenDayIndex = AxDateUtils.DAYS_OF_WEEK.findIndex((day) =>
            day.toLowerCase().startsWith(target.toLowerCase())
          );
          const dayDiff = chosenDayIndex - today.getUTCDay();
          if (dayDiff === 0) {
            return [
              today.getUTCDate(),
              today.getUTCMonth(),
              today.getUTCFullYear(),
            ];
          }
          if (direction === 'last') {
            if(dayDiff < 0){ // chosen day of the week is in the past
              today.setUTCDate(today.getUTCDate() - Math.abs(dayDiff));
            } else { // chosen day of the week is in the future
              today.setUTCDate(today.getUTCDate() - (7 - Math.abs(dayDiff)));
            }
          }
          if (direction === 'next') {
            if (dayDiff > 0) {
              today.setUTCDate(today.getUTCDate() + dayDiff);
            } else {
              today.setUTCDate(today.getUTCDate() + (7 - Math.abs(dayDiff)));
            }
          }
          return [
            today.getUTCDate(),
            today.getUTCMonth(),
            today.getUTCFullYear(),
          ];
        }

        // Test for month
        if (
          regex2or3Chars.test(target) &&
          AxDateUtils.MONTHS.some((month) =>
            month.toLowerCase().includes(target.toLowerCase())
          )
        ) {
          let targetMonth: number;
          const chosenMonthIndex = AxDateUtils.MONTHS.findIndex((month) =>
            month.toLowerCase().startsWith(target.toLowerCase())
          );
          const monthDiff = chosenMonthIndex - today.getUTCMonth();
          if (monthDiff === 0) {
            return [
              today.getUTCDate(),
              today.getUTCMonth(),
              today.getUTCFullYear(),
            ];
          }
          if (direction === 'last') {
            return [
              today.getUTCDate(),
              chosenMonthIndex,
              today.getUTCFullYear() - 1,
            ];
          }
          if (direction === 'next') {
            return [
              today.getUTCDate(),
              chosenMonthIndex,
              today.getUTCFullYear() + 1,
            ];
          }
        }
      }

      return null; // Bomb out if nothing matches
    };

    const processDateVals = (dateVals:(number | string)[]) => {
      // Check if a date value is number
      for (let i = 0; i<dateVals.length; i++) {
        const floatChar = parseFloat(dateVals[i] as string);
        if (isNaN(floatChar)) {
          // eslint-disable-next-line no-console
          console.error(dateVals[i], ' is not a number!');
          return '';
        }
      }

      const inputYear = dateVals[2] as number;
      const inputMonth = (dateVals[1] as number);
      const inputCalendarDate = dateVals[0] as number;

      const checkedDate = AxDateUtils.checkDate(
        'dateInputField',
        inputYear,
        inputMonth,
        inputCalendarDate
      ) as Date | null;
      return checkedDate;
    };

    if (e.type === 'keydown') {
      dateInputValue = (e.target as HTMLInputElement).textContent as string;

      let newDate;
      let dateVals;
      if (dateInputValue === '') {
        dateVals = getDateVals(
          `${this.workingDate.getUTCDate()}/${this.workingDate.getUTCMonth() + 1}/${this.workingDate.getUTCFullYear()}`);
      } else {
        dateVals = getDateVals(dateInputValue);
      }

      if (dateVals) {
        newDate = processDateVals(dateVals);
      }

      if (dateVals && newDate != null) {
        this.setWorkingDate(newDate as Date);
        this.isDateInputError = false;
      } else {
        this.isDateInputError = true;
      }

      this.updateDateValues();
      this.updateValues();
      this.updateStyling();
    }
  }

  private selectorBarStepAction(datePart: string, amount: number){
    let targetDate;
    const currentYear = this.workingDate.getUTCFullYear();
    const currentMonth = this.workingDate.getUTCMonth();
    const currentCalendarDate = this.workingDate.getUTCDate();

    if(datePart === "year"){
      targetDate = AxDateUtils.checkDate("yearStep",currentYear+amount, currentMonth, currentCalendarDate) as Date;
      this.setWorkingDate(targetDate);
    }

    if(datePart === "month"){
      if(currentMonth===0 && amount < 0){ // Moving back one month from Jan
        targetDate = AxDateUtils.checkDate("monthStep", currentYear-1, 11, currentCalendarDate) as Date;
      }

      if(currentMonth === 11 && amount > 0){ // Moving forward 1 month from Dec
        targetDate = AxDateUtils.checkDate("monthStep", currentYear+1, 0, currentCalendarDate) as Date;
      }

      targetDate = AxDateUtils.checkDate("monthStep", currentYear, currentMonth+amount, currentCalendarDate) as Date;
      this.setWorkingDate(targetDate);
    }

    if(this.whichWorkingDate === "from"){
      this.fromDateIsSelected = true;
      this.updateDateValues(targetDate, this.toDate);
      this.updateValues();
    };
    if(this.whichWorkingDate === "to"){
      this.toDateIsSelected = true;
      this.updateDateValues(this.fromDate, targetDate);
      this.updateValues();
    };
    this.updateStyling();
  }

  private selectorDropdownItemAction(datePart: string, newValue:number){
    let targetDate;
    const currentYear = this.workingDate.getUTCFullYear();
    const currentMonth = this.workingDate.getUTCMonth();
    const currentCalendarDate = this.workingDate.getUTCDate();

    if(datePart === "year"){
      if(this.yearBar.classList.contains("open")){
        this.yearBar.classList.remove("open");
      }

      targetDate = AxDateUtils.checkDate("yearDropdownSelect", newValue, currentMonth, currentCalendarDate) as Date;
      this.setWorkingDate(targetDate);
    }

    if(datePart === "month"){
      if(this.monthBar.classList.contains("open")){
        this.monthBar.classList.remove("open");
      }

      targetDate = AxDateUtils.checkDate("monthDropdownSelect", currentYear, newValue, currentCalendarDate) as Date
      this.setWorkingDate(targetDate);
    }

    if(this.whichWorkingDate === "from"){
      this.fromDateIsSelected = true;
      this.updateDateValues(targetDate, null);
    };

    if(this.whichWorkingDate === "to"){
      this.toDateIsSelected = true;
      this.updateDateValues(null, targetDate);
    };

    this.updateValues();
    this.updateStyling();
  }

  // ------- Event listeners -------
  private documentEventListener() {
    document.addEventListener("mouseup", (e:MouseEvent) => {
      const element = e.target as HTMLElement;

      const insideClasses = ["ax-date-picker-calendar", "ax-date-picker-calendar__content-cell"];
      const targetElementClasses = Array.from((element as HTMLElement).classList);
      if(targetElementClasses.some(element => insideClasses.includes(element))){ return; }

      const isInsideDatepicker = this.datePicker.contains(element);
      const isParentShadowDom = element?.shadowRoot?.contains(this.datePicker) && e.composedPath().includes(this.datePicker);

      if(!isInsideDatepicker && !isParentShadowDom){
        this.setDatePickerPanelExpanded("close");
        this.updateValues();
      }
    })
  }

  private datePickerEventListeners() {
    this.datePicker.addEventListener("mouseup", (e:MouseEvent) => {
      const allDatePickers = document.querySelectorAll(".ax-date-picker");
      if (allDatePickers.length > 1){
        allDatePickers.forEach(datepicker => {
          if(!datepicker.contains(e.target as HTMLElement) && datepicker.classList.contains("open") ){
            datepicker.classList.remove("open");
          }
        })
      }
    }, true);
  }

  private calendarIconEventListener() {
    const calendarIconEvent = new Event("toggleExpansionPanel");
    const calendarIcon = this.datePicker.querySelector(".ax-icon.ax-icon--calendar") as HTMLDivElement;

    calendarIcon.addEventListener("mouseup", (e:MouseEvent) => {
      if(this.datePicker?.classList.contains("disabled")){return};
      this.toggleDatePickerPanelExpanded();
    })

    calendarIcon.dispatchEvent(calendarIconEvent);
  }

  private dateInputFieldListeners(){

    const dateFieldKeydownAction = (e:KeyboardEvent | MouseEvent) => {
      if((e as KeyboardEvent).key == "Enter" || (e as KeyboardEvent).key == "Tab"){
        // e.preventDefault()
        this.dateInputFieldUpdateAction(e as KeyboardEvent);
        if(!this.isDateInputError && this.isDateRange){
          if(this.fromDateInputField.contains(e.target as HTMLElement)){
            updateWorkingDate((e as MouseEvent), this.toDateInputField, "to");
            if(!this.isDateInputError){this.fromDateIsSelected = true}
            this.updateValues();
          }
          if(this.toDateInputField.contains(e.target as HTMLElement)){
            updateWorkingDate((e as MouseEvent), this.toDateInputField, "to");
            if(!this.isDateInputError){this.toDateIsSelected = true}
            this.updateValues();
            // TODO: Where cursor goes after hitting 'enter' or 'tab' is TBD
            this.setDatePickerPanelExpanded('close');
          }
          if(!this.fromDateIsSelected){this.fromDateInputField.innerHTML = ""}
          if(!this.toDateIsSelected){this.toDateInputField.innerHTML = ""};
        }
        if(!this.isDateInputError && !this.isDateRange){
          if(this.fromDateInputField.contains(e.target as HTMLElement)){
            updateWorkingDate((e as MouseEvent), this.fromDateInputField, "from");
            if(!this.isDateInputError){this.fromDateIsSelected = true}
            this.updateValues();
          }
        }
      }
      if((e as KeyboardEvent).key === "Tab" && e.shiftKey){
        if(!this.isDateInputError && this.isDateRange){
          if(this.toDateInputField.contains(e.target as HTMLElement)){
            updateWorkingDate((e as MouseEvent), this.fromDateInputField, "from");
          }
        }
      }
    }

    const updateWorkingDate = (e: MouseEvent, inputField: HTMLDivElement, whichDate: WhichDate) => {
      this.workingDateInputField = inputField;
      this.setDatePickerPanelExpanded("open");
      if(this.isDateRange && this.whichWorkingDate != whichDate){
        this.whichWorkingDate = whichDate;
        if(this.whichWorkingDate === "from") {this.setWorkingDate(this.fromDate);}
        if(this.whichWorkingDate === "to") {this.setWorkingDate(this.toDate);}
      }

      this.updateValues();
      this.updateStyling();
      this.workingDateInputField.innerHTML = "";
    }


    // cancel slash key event
    function cancelSlashKeyEvent (e: KeyboardEvent) {
        if ((e as KeyboardEvent).key === "/") {
          e.stopPropagation();
        }
    }

    if(this.fromDateInputField){
      this.fromDateInputField.addEventListener('keyup', cancelSlashKeyEvent);
      this.fromDateInputField.addEventListener('keydown', (e: KeyboardEvent) => {
        cancelSlashKeyEvent(e);
        if(this.datePicker.classList.contains("disabled")){return}
        dateFieldKeydownAction(e);
      })

      this.fromDateInputField.addEventListener("mouseup", (e) => {
        if(this.datePicker.classList.contains("disabled")){return}
        updateWorkingDate(e, this.fromDateInputField, "from");
        return false;
      })
    }

    if(this.toDateInputField){
      this.toDateInputField.addEventListener('keyup', cancelSlashKeyEvent);
      this.toDateInputField.addEventListener('keydown', (e: KeyboardEvent) => {
        cancelSlashKeyEvent(e);
        if(this.datePicker.classList.contains("disabled")){return}
        dateFieldKeydownAction(e);
      })

      this.toDateInputField.addEventListener('mouseup', (e) => {
        if(this.datePicker.classList.contains("disabled")){return}
        updateWorkingDate(e, this.toDateInputField, "to");
        return false;
      });
    }
  }

  private yearSelectorDisplayListeners(){
    if(this.yearBar){
      const yearSelectorBar = this.datePicker.querySelector(".ax-date-picker__year-bar") as HTMLDivElement;
      const yearDisplayText = yearSelectorBar.querySelector(".year-selector__display__text") as HTMLSpanElement;
      const yearSelectorList = yearSelectorBar.querySelector(".year-selector__list-dropdown") as HTMLUListElement;

      yearDisplayText.addEventListener('mouseup', (e) => {
        this.toggleOpenCloseAction(yearSelectorBar);
        this.datePicker.querySelector(".ax-date-picker__month-bar")?.classList.remove("open");
        if(yearSelectorBar.classList.contains("open")){
          yearSelectorBar.querySelectorAll(".year-selector__list-dropdown__item").forEach(listItem => {
            if(this.workingDate.getUTCFullYear().toString() === listItem.textContent){
              const offSetValue = 40 * (parseInt(listItem.textContent) - this.yearListStart);
              yearSelectorList.scrollTop = offSetValue;
            }
          })
        }
      })

      yearSelectorBar.addEventListener('mouseup', e => {
        const elementClasses = (e.target as HTMLElement)?.classList;
        if( elementClasses.contains("ax-date-picker-event--year-selector--previous")){
          this.selectorBarStepAction("year", -1)
        }
        if( elementClasses.contains("year-selector__list-dropdown__item")){
          const listItem=e.target as HTMLLIElement;
          const selectedYear = parseInt(listItem.innerText);
          if(yearSelectorBar.classList.contains("open"))
          this.selectorDropdownItemAction("year", selectedYear)
        }
        if( elementClasses.contains("ax-date-picker-event--year-selector--next")){
          this.selectorBarStepAction("year", 1)
        }
      })
    }
  }

  private monthSelectorDisplayListeners(){
    if(this.monthBar){
    const monthSelectorBar = this.datePicker.querySelector(".ax-date-picker__month-bar") as HTMLDivElement;
    const monthDisplayText = this.datePicker.querySelector(".month-selector__display__text") as HTMLSpanElement;
    const monthSelectorList = monthSelectorBar.querySelector(".month-selector__list-dropdown") as HTMLUListElement;

    monthDisplayText.addEventListener('mouseup', (e) => {
      this.toggleOpenCloseAction(monthSelectorBar);
      this.datePicker.querySelector(".ax-date-picker__year-bar")?.classList.remove("open");
      if(monthSelectorBar.classList.contains("open")){
        monthSelectorBar.querySelectorAll(".month-selector__list-dropdown__item").forEach(listItem => {
          if(AxDateUtils.MONTHS[this.workingDate.getUTCMonth()] === (listItem.textContent)){
            monthSelectorList.scrollTop = 40 * this.workingDate.getUTCMonth();
          }
        })
      }
    })
    monthSelectorBar.addEventListener('mouseup', (e) => {
      const elementClasses = (e.target as HTMLElement)?.classList;
      if( elementClasses.contains("ax-date-picker-event--month-selector--previous")){
        this.selectorBarStepAction("month", -1);
      }
      if( elementClasses.contains("month-selector__list-dropdown__item")){
        const listItem=e.target as HTMLLIElement;
        const selectedMonth = AxDateUtils.MONTHS.indexOf(listItem.innerText);
        this.selectorDropdownItemAction("month", selectedMonth);
      }
      if( elementClasses.contains("ax-date-picker-event--month-selector--next")){
        this.selectorBarStepAction("month", 1);
      }
    })}
  }

  private calendarListeners(calendar: HTMLDivElement){
    const calendarDays = calendar.querySelectorAll(".ax-date-picker-calendar__content-cell");
    calendarDays.forEach(day => {
      day.addEventListener('mouseup', e=>{
          this.isDateInputError = false;

          const dateCell = e.target as HTMLDivElement;
          const newDate = parseInt(dateCell.innerText);
          let newMonth = this.workingDate.getUTCMonth();
          let newYear = this.workingDate.getUTCFullYear();

          if(dateCell.classList.contains("ax-date-picker-calendar__content-cell--previous-month")){
            newMonth = newMonth-1;
            if(this.workingDate.getUTCMonth() === 0){ // if January
              newMonth = 11;
              newYear = newYear-1;
            }
          }

          if(dateCell.classList.contains("ax-date-picker-calendar__content-cell--next-month")){
            newMonth = newMonth + 1;
            if(this.workingDate.getUTCMonth() === 11){ // if December
              newMonth = 0;
              newYear = newYear + 1;
            }
          }
          this.setWorkingDate(makeUTCDate(newYear, newMonth, newDate)); // Update the working date
          if(this.whichWorkingDate === "from"){
            this.fromDateIsSelected = true;
            this.updateDateValues(this.workingDate, null);
          }
          if(this.whichWorkingDate === "to"){
            this.toDateIsSelected = true
            this.updateDateValues(null, this.workingDate);
          }

          if(!this.isDateRange){
            this.setDatePickerPanelExpanded("close");
          }

          this.updateValues(); // Update fields with correct values
          this.updateStyling();
      })
    })


  }

  private sidebarItemEventListeners(){
    if(!this.expansionSidebar){
      return;
    }

    const clearSelected = (items:HTMLLIElement[]) => {
      items.forEach(item => {
        item.classList.remove("selected")
      })
    }

    const allSidebarItems = Array.from(this.expansionSidebar.querySelectorAll('li[class$="__content__item"]')) as HTMLLIElement[];

    if(this.financialYearSidebar){
      const fyItems = this.financialYearSidebar.querySelectorAll(".sidebar--financial-years__content__item");
      fyItems.forEach(item => {
        item.addEventListener('mouseup',(e) => {
          const listItem = e.target as HTMLLIElement;
          const itemContent = listItem.textContent;

          if(itemContent) {
            const fromYear = itemContent.substring(0,4);
            const toYear = itemContent.substring(0,2) + itemContent.slice(-2);
            const fromDate = makeUTCDate(parseInt(fromYear),6, 1);
            const toDate = makeUTCDate(parseInt(toYear), 6, 0 );

            clearSelected(Array.from(allSidebarItems) as HTMLLIElement[])
            listItem.classList.add("selected");

            this.fromDateIsSelected = true;
            this.toDateIsSelected = true;

            this.updateDateValues(fromDate, toDate);
            this.updateValues();
            this.updateStyling();
          }
        })
      })
    }

    if(this.quartersSidebar){
      const qItems = this.quartersSidebar.querySelectorAll(".sidebar--quarters__content__item");
      qItems.forEach(item=>{
        item.addEventListener('mouseup',(e)=>{
          const listItem = e.target as HTMLLIElement;
          const listItemContent = listItem.textContent as string;
          const fullYear = parseInt(listItem.getAttribute("data-year") as string);
          const quarter = parseInt(listItemContent.substring(1,2));
          const quarterDates = AxDateUtils.getQuarterDates(fullYear, quarter) as Date[];

          clearSelected(Array.from(allSidebarItems) as HTMLLIElement[]);
          listItem.classList.add("selected");

          this.fromDateIsSelected = true;
          this.toDateIsSelected = true;

          this.updateDateValues(quarterDates[0], quarterDates[1]);
          this.updateValues();
          this.updateStyling();
        })
      })
    }
  }

  public addEventListener(name:string, eventHandler: (e: CustomEvent) => void) {
    if (!this.eventListeners[name]) {
      this.eventListeners[name] = [];
    }
    this.eventListeners[name].push(eventHandler);
  }

  // ------- Builds -------
  private buildYearSelector(){
      if(this.yearBar){
      const yearSelectorChildrenStructure = `
      <div class="year-selector">
        <div class="year-selector__icon-container ax-date-picker-event--year-selector--previous">
          <i class="ax-icon ax-icon--chevron-right ax-date-picker-event--year-selector--previous"></i>
        </div>
        <div class="year-selector__display">
          <span class="year-selector__display__text"></span>
        </div>
        <div class="year-selector__icon-container ax-date-picker-event--year-selector--next">
          <i class="ax-icon ax-icon--chevron-left ax-date-picker-event--year-selector--next"></i>
        </div>
      </div>
      <div class="year-selector__list">
      </div>`

      this.yearBar.innerHTML = yearSelectorChildrenStructure;
      this.yearDisplayText = this.datePicker.querySelector(".year-selector__display__text") as HTMLSpanElement;

      const yearSelectorList = this.datePicker.querySelector(".year-selector__list") as HTMLDivElement;
      const yearDropdown = document.createElement("ul");

      yearDropdown.classList.add("year-selector__list-dropdown")
      for(let i=this.yearListStart; i<this.yearlistEnd; i++){
        const listItem = document.createElement("li");
        listItem.classList.add("year-selector__list-dropdown__item");
        listItem.textContent = i.toString();
        yearDropdown.appendChild(listItem);
      }
      yearSelectorList.appendChild(yearDropdown);
    }
  }

  private buildMonthSelector(){
    if(this.monthBar){
      const monthSelectorChildrenStructure = `
      <div class="month-selector">
        <div class="month-selector__icon-container ax-date-picker-event--month-selector--previous">
          <i class="ax-icon ax-icon--chevron-right ax-date-picker-event--month-selector--previous"></i>
        </div>
        <div class="month-selector__display">
          <span class="month-selector__display__text"></span>
        </div>
        <div class="month-selector__icon-container ax-date-picker-event--month-selector--next">
          <i class="ax-icon ax-icon--chevron-left ax-date-picker-event--month-selector--next"></i>
        </div>
      </div>
      <div class="month-selector__list">
      </div>`;

      this.monthBar.innerHTML = monthSelectorChildrenStructure;
      this.monthDisplayText = this.datePicker.querySelector(".month-selector__display__text") as HTMLSpanElement;

      const monthSelectorList = this.datePicker.querySelector(".month-selector__list") as HTMLDivElement;
      const monthDropdown = document.createElement("ul");

      monthDropdown.classList.add("month-selector__list-dropdown");

      for(let i=0; i<AxDateUtils.MONTHS.length; i++){
        const listItem = document.createElement("li");
        listItem.classList.add("month-selector__list-dropdown__item");
        listItem.textContent=AxDateUtils.MONTHS[i];
        monthDropdown.appendChild(listItem);
      }
      monthSelectorList?.appendChild(monthDropdown);
    }
  }

  private buildExpansionGap(){
    const yearSelectorBar = this.datePicker.querySelector(".ax-date-picker__year-bar");
    const expansionPanelContent = this.datePicker.querySelector(".ax-date-picker__expansion-panel__content");

    // Build the expansion gap
    const gapDiv = document.createElement("div");
    gapDiv.setAttribute('class', "ax-date-picker__expansion-gap");

    const expansionGapSections =
      '<div class="ax-date-picker__expansion-gap__from"></div>' +
      '<div class="ax-date-picker__expansion-gap__to"></div>'
    gapDiv.innerHTML = expansionGapSections;

    if(expansionPanelContent){
      expansionPanelContent.insertBefore(gapDiv, yearSelectorBar);
    }
  }

  private buildCalendar() {
    const calendarSection = this.datePicker.querySelector
      (".ax-date-picker__calendar-section") as HTMLDivElement;
    const currentCalendar = calendarSection.querySelector(".ax-date-picker-calendar") as HTMLDivElement;
    if(currentCalendar){currentCalendar.remove()};

    const calendar = AxDateUtils.createCalendarBody(this.workingDate);
    this.calendarListeners(calendar);
    calendarSection.appendChild(calendar);
  }

  private buildSidebars(){
    if(this.expansionSidebar){
      if(this.expansionSidebar.querySelector(".sidebar--financial-years")){
        this.financialYearSidebar = this.expansionSidebar.querySelector(".sidebar--financial-years") as HTMLDivElement;
        const fyStructure = `
          <div class="sidebar--financial-years__header">Financial Years</div>
          <ul class="sidebar--financial-years__content"></ul>
        `

        this.financialYearSidebar.innerHTML = fyStructure;
        const financialYearList = this.financialYearSidebar.querySelector("ul.sidebar--financial-years__content") as HTMLLIElement;
        for (let i = this.fyEnd; i>= this.fyStart; i--){
          const listItem = document.createElement("li")
          listItem.classList.add("sidebar--financial-years__content__item")
          listItem.textContent = (i-1).toString() + " - " + i.toString().slice(-2);
          financialYearList?.appendChild(listItem);
        }
      }

      if(this.expansionSidebar.querySelector(".sidebar--quarters")){
        this.quartersSidebar = this.expansionSidebar.querySelector(".sidebar--quarters") as HTMLDivElement;
        const qStructure = `
          <div class="sidebar--quarters__header">Quarters</div>
          <ul class="sidebar--quarters__content"></ul>
        `

        this.quartersSidebar.innerHTML = qStructure;
        const quartersList = this.quartersSidebar.querySelector("ul.sidebar--quarters__content") as HTMLLIElement;
        const listStart = this.qStart;
        const listEnd = this.qEnd;

        let quarters = [];

        for(let i = listEnd[0]; i>= listStart[0]; i--){ // year calculator
          if(i === listEnd[0]){
            for(let j = listEnd[1]; j>0; j--){
              quarters.push([i, j]);
            }
          }

          else if(i === listStart[0]){
            for(let j = 4; j >= listStart[1]; j--){
              quarters.push([i, j]);
            }
          }

          else {
            for(let j = 4; j>0; j--){
              quarters.push([i, j]);
            }
          }
        }

        for (let n=0; n<=quarters.length-1; n++){
          const quarterVal = "Q" + quarters[n][1].toString() +
            " - " +
            quarters[n][0].toString().slice(-2);
          const listItem = document.createElement("li");
          listItem.setAttribute("data-year", quarters[n][0].toString());
          listItem.classList.add("sidebar--quarters__content__item");
          // listItem.classList.add("sidebar--quarters__content__item", "fw-semibold");
          listItem.textContent = quarterVal;
          quartersList.appendChild(listItem);
        }
      }
    }
  }

  // ------- Helpers -------
  private getInputFieldValue = (option: string, date?: Date) => {
    if(option === "blank") {
      return "";
    }
    if(option === "dateformat") {
      return this.dateInputFormatString;
    }
    if(date && option === "datevalue") {
      return this.dateInputFieldContent(date) as string;
    }
  }

  private checkDateRangeError(){
    if(this.fromDateIsSelected && this.toDateIsSelected){
      this.isDateRangeError = false;
      return;
    }
    if(this.isDateRange && this.fromDateIsSelected && this.toDateIsSelected){
      if(this.toDate < this.fromDate){
        this.isDateRangeError = true;
      } else {
        this.isDateRangeError = false;
      }
    }
  }

  private isLegitYear(data: number | string){
    let testYearValue = 0;
    let legitYear;
    const legitYearMinimum = 1900;
    const legitYearMaximum = 2300;

    testYearValue = typeof data === "string"? parseInt(data) : data;

    if(testYearValue.toString().length===4) {
      if(testYearValue >= legitYearMinimum && testYearValue <= legitYearMaximum) {
        legitYear = data;
        return true;
      }
    }
    return false;
  }

  private dateInputFieldContent(date?:Date){
    if(!date){return}

    let dateStrArr = AxDateUtils.getFormattedDate('ddmmyyyy', date).toString().split("/");
    dateStrArr = dateStrArr.map(elem => elem.trim());

    const dateInputFieldInnerHtml =
      `<span class="ax-date-picker__date-value">${dateStrArr[0]}</span>`+
      `<span class="ax-date-picker__delimiter">/</span>`+
      `<span class="ax-date-picker__date-value">${dateStrArr[1]}</span>`+
      `<span class="ax-date-picker__delimiter">/</span>`+
      `<span class="ax-date-picker__date-value">${dateStrArr[2]}</span>`

    return dateInputFieldInnerHtml;
  }

  private updateValues(fromDate?:Date|null, toDate?: Date|null ) {
    if(this.isDateRange){
      this.checkDateRangeError();
      if(this.isDateInputError) {
        if(this.whichWorkingDate === "from") {
          this.fromDateInputField.innerHTML = "";
          this.toDateInputField.innerHTML = this.dateInputFieldContent(this.toDate) as string;
        }
        else if(this.whichWorkingDate === "to") {
          this.fromDateInputField.innerHTML = this.dateInputFieldContent(this.fromDate) as string;
          this.toDateInputField.innerHTML = "";
        }
        return;
      }

      this.fromDateInputField.innerHTML = this.fromDateIsSelected?
        this.dateInputFieldContent(this.fromDate) as string :
        this.getInputFieldValue(this.inputFieldsInitialValue as string, this.fromDate) as string;
      this.toDateInputField.innerHTML = this.toDateIsSelected?
        this.dateInputFieldContent(this.toDate) as string :
        this.getInputFieldValue(this.inputFieldsInitialValue as string, this.toDate) as string;

    } else {
      if(this.isDateInputError) {
        this.fromDateInputField.innerHTML = "";
        return;
      } else {
        this.fromDateInputField.innerHTML = this.fromDateIsSelected ?
          this.dateInputFieldContent(this.fromDate) as string :
          this.dateInputFormatString;
      }
    }

    if(this.yearDisplayText){
      this.yearDisplayText.innerText = this.workingDate.getUTCFullYear().toString();
    }

    if(this.monthDisplayText){
      this.monthDisplayText.innerText = AxDateUtils.getFormattedDate("monthName", this.workingDate).toString();
    }
    this.buildCalendar();
  }

  private updateStyling() {
    if(this.whichWorkingDate === "from"){
      this.datePicker.querySelector(".ax-date-input--from-tab")?.classList.add("active-tab");
      this.datePicker.querySelector(".ax-date-input--to-tab")?.classList.remove("active-tab");

      this.datePicker.querySelector(".ax-date-picker__expansion-gap__from")?.classList.add("active-tab");
      this.datePicker.querySelector(".ax-date-picker__expansion-gap__to")?.classList.remove("active-tab");

      this.yearBar.classList.add("from");
      this.yearBar.classList.remove("to");
    }

    if(this.whichWorkingDate === "to"){
      this.datePicker.querySelector(".ax-date-input--from-tab")?.classList.remove("active-tab")
      this.datePicker.querySelector(".ax-date-input--to-tab")?.classList.add("active-tab")

      this.datePicker.querySelector(".ax-date-picker__expansion-gap__from")?.classList.remove("active-tab");
      this.datePicker.querySelector(".ax-date-picker__expansion-gap__to")?.classList.add("active-tab");

      this.yearBar.classList.remove("from");
      this.yearBar.classList.add("to");
    }

    if(this.isDateRange
      && this.toDateInputField.parentElement
      && Array.from(this.toDateInputField.parentElement.classList).includes("ax-date-input--to-tab")){
        const toDateTab = this.toDateInputField.parentElement as HTMLDivElement;
        const toDateTabClasses = Array.from(toDateTab.classList)
        if(this.isDateRangeError) {
          if(!toDateTabClasses.includes("error")){
            toDateTab.classList.add("error");
          }
        } else {
          if(toDateTabClasses.includes("error")){
            toDateTab.classList.remove("error");
          }
        }
    }
  }

  private updateDateValues(fromDate?: Date|null, toDate?: Date|null) {
    let oldFromDate = fromDate?  this.fromDate : fromDate;
    let oldToDate = toDate? this.toDate: toDate;

    if(fromDate && toDate){
      this.fromDate = fromDate;
      this.toDate = toDate;
      if(this.whichWorkingDate === "from") {
        this.setWorkingDate(fromDate);
      }
      if(this.whichWorkingDate === "to") {
        this.setWorkingDate(toDate);
      }
    } else {
      if(this.whichWorkingDate === "from"){
        this.fromDate = this.workingDate;
      }
      if(this.whichWorkingDate === "to"){
        this.toDate = this.workingDate;
      }
    }


    if(oldFromDate != this.fromDate){
      const dateChangeEvent = new CustomEvent("change",{
        detail: {
          name: "fromDate",
          value: this.fromDate,
          oldValue: oldFromDate,
        },
      });
      this.eventListeners?.["change"]?.forEach((listener)=>{
        listener(dateChangeEvent)
      })
    }

    if(oldToDate != this.toDate){
      const dateChangeEvent = new CustomEvent("change",{
        detail: {
          name: "toDate",
          value: this.toDate,
          oldValue: oldToDate,
        },
      });
      this.eventListeners?.["change"]?.forEach((listener)=>{
        listener(dateChangeEvent)
      })
    }
  }


  // ------- Initialisation -------
  public init(){

    this.buildYearSelector();
    this.buildMonthSelector();
    this.buildExpansionGap();
    this.buildCalendar();
    this.buildSidebars();

    this.updateValues();
    this.updateStyling();

    this.yearSelectorDisplayListeners();
    this.monthSelectorDisplayListeners();
    this.dateInputFieldListeners();
    this.sidebarItemEventListeners();
    this.documentEventListener();
    this.datePickerEventListeners();
    this.calendarIconEventListener();
    return this;
  }
}
